import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConfirmDialog from "components/popup/ConfirmDialog";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { DeletedHoliday, Holiday, HolidayBody, HolidayKind } from "hooks/console/useConsoleHoliday";
import { DAY_STATE_LABEL, getDayState, isWeekendStr, NAME_MAX, TIME_SLOTS, WEEKDAY_KO } from "./holidayUtils";

type HolidayEditDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dateStr: string;
    holiday?: Holiday;
    readOnly: boolean; // 지난 날짜
    isSubmitting: boolean;
    onSubmit: (body: HolidayBody) => void;
    onDelete: (close: () => void) => void;
    deletedHoliday?: DeletedHoliday; // 이 날짜의 삭제된 공휴일 — 있으면 "공휴일로 복원" 제공
    onRestore: () => void;
    isRestoring: boolean;
};

// 휴무일 등록/수정/읽기 전용 모달
export default function HolidayEditDialog({
    open,
    onOpenChange,
    dateStr: dateProp,
    holiday: holidayProp,
    readOnly: readOnlyProp,
    isSubmitting,
    onSubmit,
    onDelete,
    deletedHoliday: deletedProp,
    onRestore,
    isRestoring,
}: HolidayEditDialogProps) {
    // 닫힘 애니메이션 동안 부모가 선택 날짜를 비워도 마지막 내용을 유지(제목·날짜 깜박임 방지)
    const [shown, setShown] = useState({ dateStr: dateProp, holiday: holidayProp, readOnly: readOnlyProp, deleted: deletedProp });
    useEffect(() => {
        if (open) setShown({ dateStr: dateProp, holiday: holidayProp, readOnly: readOnlyProp, deleted: deletedProp });
    }, [open, dateProp, holidayProp, readOnlyProp, deletedProp]);
    const { dateStr, holiday, readOnly, deleted } = shown;
    // 주말은 직접 등록 불가(자동 휴무) — 삭제된 공휴일 복원만 제공
    const restoreOnly = !holiday && !!deleted && !!dateStr && isWeekendStr(dateStr);

    const [kind, setKind] = useState<HolidayKind>("off");
    const [name, setName] = useState("");
    const [endTime, setEndTime] = useState("15:00");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setKind(holidayProp?.kind ?? "off");
        setName(holidayProp?.name ?? "");
        setEndTime(holidayProp?.end_time ?? "15:00");
        setError("");
    }, [open, holidayProp]);

    const dow = dateStr ? WEEKDAY_KO[new Date(`${dateStr}T00:00:00Z`).getUTCDay()] : "";
    const title = readOnly ? "휴무일 정보" : holiday ? "휴무일 수정" : "휴무일 등록";

    const submit = () => {
        const trimmed = name.trim();
        if (!trimmed) return setError("명칭을 입력해주세요.");
        if (kind === "early" && !endTime) return setError("종료시간을 선택해주세요.");
        onSubmit({ name: trimmed, kind, end_time: kind === "early" ? endTime : null });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white p-0 gap-0 w-[calc(100%-32px)] max-w-[480px] rounded-[10px]">
                <DialogHeader className="border-b border-[#dddddd] px-[24px] py-[20px] text-left">
                    <DialogTitle className="text-[20px]">{title}</DialogTitle>
                    <DialogDescription className="text-[15px] text-[#666666]">
                        {dateStr} ({dow})
                        {holiday &&
                            ` · ${DAY_STATE_LABEL[getDayState(dateStr, holiday)]}${holiday.source === "api" ? " (공공데이터)" : ""}`}
                    </DialogDescription>
                </DialogHeader>

                <div className="px-[24px] py-[20px] flex flex-col gap-[18px]">
                    {readOnly && <p className="text-[14px] text-[#999999]">지난 날짜는 조회만 가능합니다.</p>}

                    {/* 삭제된 공휴일 복원 */}
                    {deleted && !holiday && !readOnly && (
                        <div className="flex flex-wrap items-center justify-between gap-[8px] rounded-[8px] bg-[#FFF1F2] px-[14px] py-[12px]">
                            <span className="text-[14px] text-[#9F1239]">
                                삭제된 공휴일: <b className="font-[600]">{deleted.name}</b>
                            </span>
                            <button
                                type="button"
                                disabled={isRestoring}
                                onClick={onRestore}
                                className="bg-[#E11D48] text-white text-[14px] rounded-[5px] px-[12px] h-[36px] disabled:opacity-50"
                            >
                                공휴일로 복원
                            </button>
                        </div>
                    )}
                    {deleted && !holiday && !readOnly && !restoreOnly && (
                        <p className="text-[13px] text-[#999999] -mt-[8px]">또는 아래에서 다른 휴무로 직접 등록할 수 있습니다.</p>
                    )}

                    {!restoreOnly && (
                        <>
                            {/* 구분 */}
                            <fieldset disabled={readOnly} className="flex flex-col gap-[8px]">
                                <legend className="text-[14px] font-[600] text-[#222222] mb-[8px]">구분</legend>
                                <div className="flex gap-[20px]">
                                    {(
                                        [
                                            ["off", "종일 휴무"],
                                            ["early", "조기 종료"],
                                        ] as [HolidayKind, string][]
                                    ).map(([value, label]) => (
                                        <label key={value} className="flex items-center gap-[6px] text-[15px] cursor-pointer">
                                            <input
                                                type="radio"
                                                name="holiday-kind"
                                                value={value}
                                                checked={kind === value}
                                                onChange={() => setKind(value)}
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            {/* 종료시간 (조기 종료만) */}
                            {kind === "early" && (
                                <div className="flex flex-col gap-[8px]">
                                    <span className="text-[14px] font-[600] text-[#222222]">업무 종료시간</span>
                                    <Select value={endTime} onValueChange={setEndTime} disabled={readOnly}>
                                        <SelectTrigger className="relative shadow-none text-[15px] flex gap-[8px] rounded-[5px] border border-[#E1E2E4] bg-white h-[44px] w-[140px]">
                                            <SelectValue placeholder="시간" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white max-h-[260px]">
                                            {TIME_SLOTS.map(t => (
                                                <SelectItem key={t} value={t}>
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* 명칭 */}
                            <label className="flex flex-col gap-[8px]">
                                <span className="text-[14px] font-[600] text-[#222222]">
                                    명칭{" "}
                                    <span className="font-[400] text-[#999999]">
                                        ({name.length}/{NAME_MAX})
                                    </span>
                                </span>
                                <input
                                    type="text"
                                    value={name}
                                    maxLength={NAME_MAX}
                                    disabled={readOnly}
                                    onChange={e => {
                                        setName(e.target.value);
                                        setError("");
                                    }}
                                    placeholder={kind === "early" ? "예: 추석 전날 조기종료" : "예: 임시휴무"}
                                    className="border border-[#E1E2E4] rounded-[5px] px-[12px] h-[44px] text-[15px] focus:outline-none focus:ring-2 disabled:bg-[#F7F7F7]"
                                />
                            </label>
                        </>
                    )}

                    {error && <p className="text-[14px] text-[#FF5463]">{error}</p>}
                </div>

                <div className="flex items-center justify-between gap-[8px] border-t border-[#dddddd] px-[24px] py-[16px]">
                    <div>
                        {holiday && !readOnly && (
                            <ConfirmDialog
                                title={`${dateStr} 휴무일 삭제`}
                                trigger={openConfirm => (
                                    <button
                                        type="button"
                                        onClick={openConfirm}
                                        className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[14px] h-[40px]"
                                    >
                                        삭제
                                    </button>
                                )}
                                handleConfirm={onDelete}
                            />
                        )}
                    </div>
                    <div className="flex gap-[8px]">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="border border-[#dddddd] text-[14px] rounded-[5px] px-[14px] h-[40px]"
                        >
                            {readOnly ? "닫기" : "취소"}
                        </button>
                        {!readOnly && !restoreOnly && (
                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={submit}
                                className={twMerge(
                                    "bg-black text-white text-[14px] rounded-[5px] px-[14px] h-[40px]",
                                    isSubmitting && "opacity-50",
                                )}
                            >
                                저장
                            </button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
